import { create } from 'zustand';
import {
    GoogleAuthProvider,
    signInWithPopup,
    signInWithCredential,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    OAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { clearCloudStorage } from '../utils/storage';

const googleProvider = new GoogleAuthProvider();

// Detect Tauri environment — WebView2 blocks popups
const isTauri = () => typeof window !== 'undefined' && !!window.__TAURI_INTERNALS__;

const useAuthStore = create((set) => ({
    user: null,
    loading: true,
    error: null,

    // Initialize auth listener — call once on app mount
    initAuth() {
        onAuthStateChanged(auth, (user) => {
            set({
                user: user ? {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                } : null,
                loading: false,
            });
        });
    },

    async signInWithGoogle() {
        set({ error: null, loading: true });
        try {
            if (isTauri()) {
                await signInWithGoogleTauri();
            } else {
                // Popup flow for browser
                const result = await signInWithPopup(auth, googleProvider);
                return result.user;
            }
        } catch (error) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    async signOut() {
        try {
            await firebaseSignOut(auth);
            clearCloudStorage();
            // Force reload to completely dump Zustand in-memory states
            window.location.href = '/login';
        } catch (error) {
            set({ error: error.message });
        }
    },
}));

/**
 * Tauri-specific Google Sign-In flow:
 * 1. Start a localhost OAuth server (Rust side — random port)
 * 2. Open Google OAuth consent screen in system browser
 * 3. Google redirects to localhost with auth code
 * 4. Exchange auth code for ID token via Google's token endpoint
 * 5. Use signInWithCredential to authenticate with Firebase
 */
async function signInWithGoogleTauri() {
    const { invoke } = await import('@tauri-apps/api/core');
    const { listen } = await import('@tauri-apps/api/event');
    const { openUrl } = await import('@tauri-apps/plugin-opener');

    // Step 1: Listen for the port event, then start the OAuth server
    const portPromise = new Promise((resolve) => {
        listen('oauth-port', (event) => {
            resolve(event.payload);
        });
    });

    // Start the Rust-side OAuth listener (this blocks until callback is received)
    const callbackPromise = invoke('start_oauth_flow');

    // Wait for the port to be emitted
    const port = await portPromise;
    const redirectUri = `http://127.0.0.1:${port}`;

    // Step 2: Build Google OAuth URL
    // Use Firebase's API key and Google's OAuth endpoint
    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
        throw new Error('VITE_GOOGLE_OAUTH_CLIENT_ID not set in .env — see Tauri auth setup docs');
    }

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'token');
    authUrl.searchParams.set('scope', 'openid email profile');
    authUrl.searchParams.set('prompt', 'select_account');

    // Step 3: Open in system browser
    await openUrl(authUrl.toString());

    // Step 4: Wait for the callback
    const callbackPath = await callbackPromise;

    // Parse the access token from the callback URL
    // Google sends tokens in the URL fragment (#access_token=...)
    // But since our server receives a GET request, the fragment is NOT sent to the server.
    // So we need to use response_type=token and have the browser-side redirect
    // Actually, with implicit grant (response_type=token), the token is in the fragment
    // which the server can't see. We need response_type=code instead.

    // Let's switch to authorization code flow with response_type=code
    // Actually, for implicit flow the fragment stays in the browser.
    // For the server to receive it, we need the code flow.

    // Parse query string from the callback path
    const params = new URLSearchParams(callbackPath.split('?')[1] || '');
    const code = params.get('code');
    const accessToken = params.get('access_token');

    if (accessToken) {
        // Implicit grant flow — sign in directly with access token
        const credential = GoogleAuthProvider.credential(null, accessToken);
        await signInWithCredential(auth, credential);
    } else if (code) {
        // Authorization code flow — need to exchange code for tokens
        // Exchange the code for tokens using Google's token endpoint
        const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                code,
                client_id: clientId,
                redirect_uri: redirectUri,
                grant_type: 'authorization_code',
            }),
        });
        const tokenData = await tokenResponse.json();

        if (tokenData.error) {
            throw new Error(tokenData.error_description || tokenData.error);
        }

        const credential = GoogleAuthProvider.credential(tokenData.id_token);
        await signInWithCredential(auth, credential);
    } else {
        throw new Error('No auth code or access token received from Google');
    }
}

export default useAuthStore;
